import { cloneElement, useMemo, ChangeEvent, ForwardedRef, ReactElement, Ref, useCallback } from 'react';
import { ChevronDownIcon } from '@heroui/shared-icons';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { AnimatePresence } from 'framer-motion';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import {
  Listbox,
  FreeSoloPopover,
  Spinner,
  forwardRef,
  ScrollShadow,
  HiddenSelect,
  UseSelectProps,
  useSelect,
} from '@heroui/react';
import SearchInput from '../SearchInput';

interface Props<T> extends UseSelectProps<T> {
  searchValue?: string;
  onSearchValue?: (value: string) => void;
  endPopoverContent?: ReactElement;
  showCheckmarkIcon?: boolean;
}

const getDefaultStyles = <T,>(props: Props<T>): Props<T> => {
  const { isInvalid, label, startContent, showCheckmarkIcon } = props;

  return {
    ...props,
    selectorIcon: (
      <span>
        <MdOutlineKeyboardArrowDown size={24} className="tmdx-select__icon-arrow" />
      </span>
    ),
    classNames: {
      base: ['tmdx-select', isInvalid && 'tmdx-select--invalid', 'shadow-none'],
      trigger: [
        'bg-base-60',
        'data-[focus=true]:bg-base-40',
        'data-[invalid=true]:bg-red-80',
        'tmdx-select__trigger',
        'rounded-lg',
        'shadow-none',
        startContent && 'hasStartContent',
        !label && 'min-h-8 h-8',
        !!label && 'hasLabel',
      ],
      value: ['text-base-30', 'text-m-r!', 'data-[invalid=true]:text-base-5!', 'tmdx-select__value'],
      label: ['tmdx-select__label', 'group-focus-within:text-base-10', isInvalid && 'text-danger!'],
      innerWrapper: ['bg-transparent', 'gap-2', !!label && 'pt-0', !!label && 'pb-0.5', !!label && 'items-end'],
      listbox: 'tmdx-select__listbox p-0',
      listboxWrapper: 'p-0',
      popoverContent: 'bg-base-60 shadow-xl rounded-lg p-1, min-w-40',
      selectorIcon:
        'text-base-20 w-8 h-8 m-auto flex items-center justify-start right-0 top-0 bottom-0 data-[open=true]:rotate-0 [&[data-open="true"]>svg]:rotate-180 [&>svg]:transition-transform [&>svg]:duration-300 [&>svg]:ease [&>svg]:motion-reduce:transition-none',
      description: ['text-base-20', 'text-s-r', 'form-item__description', isInvalid && 'text-danger!'],
    },
    listboxProps: {
      itemClasses: {
        base: `px-3 py-2 *:text-m-r! rounded-lg *:text-base-5 data-[hover=true]:bg-base-50! data-[selected=true]:bg-base-50! [&[data-selected=true]_[data-hover=true]]:bg-base-50! ${!showCheckmarkIcon ? '[&[data-selected=true]_[aria-hidden=true]]:hidden' : ''}`,
      },
    },
  };
};

const CustomSelect = <T extends object>(
  { onSearchValue, searchValue = '', endPopoverContent, ...props }: Props<T>,
  ref: ForwardedRef<HTMLSelectElement>,
) => {
  const styledProps = getDefaultStyles({ ...props, isVirtualized: false });

  const {
    Component,
    state,
    label,
    hasHelper,
    isLoading,
    triggerRef,
    selectorIcon = <ChevronDownIcon />,
    description,
    errorMessage,
    isInvalid,
    startContent,
    endContent,
    placeholder,
    renderValue,
    shouldLabelBeOutside,
    disableAnimation,
    getBaseProps,
    getLabelProps,
    getTriggerProps,
    getValueProps,
    getListboxProps,
    getPopoverProps,
    getSpinnerProps,
    getMainWrapperProps,
    getInnerWrapperProps,
    getHiddenSelectProps,
    getHelperWrapperProps,
    getListboxWrapperProps,
    getDescriptionProps,
    getErrorMessageProps,
    getSelectorIconProps,
  } = useSelect<T>({ ...styledProps, ref });

  // eslint-disable-next-line jsx-a11y/label-has-associated-control
  const labelContent = label ? <label {...getLabelProps()}>{label}</label> : null;

  const clonedIcon = cloneElement(selectorIcon as ReactElement, getSelectorIconProps());

  const helperWrapper = useMemo(() => {
    const shouldShowError = isInvalid && errorMessage;
    const hasContent = shouldShowError || description;

    if (!hasHelper || !hasContent) return null;

    return (
      <div {...getHelperWrapperProps()}>
        {shouldShowError ? (
          <div {...getErrorMessageProps()}>{errorMessage}</div>
        ) : (
          <div {...getDescriptionProps()}>{description}</div>
        )}
      </div>
    );
  }, [
    hasHelper,
    isInvalid,
    errorMessage,
    description,
    getHelperWrapperProps,
    getErrorMessageProps,
    getDescriptionProps,
  ]);

  const renderSelectedItem = useMemo(() => {
    if (!state.selectedItems?.length) return placeholder;

    if (renderValue && typeof renderValue === 'function') {
      const mappedItems = [...state.selectedItems].map((item) => ({
        key: item.key,
        data: item.value,
        type: item.type,
        props: item.props,
        textValue: item.textValue,
        rendered: item.rendered,
        'aria-label': item['aria-label'],
      }));

      return renderValue(mappedItems);
    }

    return state.selectedItems.map((item) => item.textValue).join(', ');
  }, [state.selectedItems, renderValue, placeholder]);

  const renderIndicator = useMemo(() => {
    if (isLoading) {
      return <Spinner {...getSpinnerProps()} ref={getSpinnerProps().ref as Ref<HTMLDivElement>} />;
    }

    return clonedIcon;
  }, [isLoading, clonedIcon, getSpinnerProps]);

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onSearchValue?.(event.target.value);
    },
    [onSearchValue],
  );

  const popoverContent = useMemo(
    () =>
      state.isOpen ? (
        <FreeSoloPopover {...getPopoverProps()}>
          {!!onSearchValue && (
            <div className="mb-2 py-2 px-1 w-full">
              <SearchInput
                placeholder="Search"
                value={searchValue}
                onChange={onSearchChange}
                isClearable
                onClear={() => onSearchValue('')}
                autoFocus
              />
            </div>
          )}
          <ScrollShadow {...getListboxWrapperProps()} ref={getListboxWrapperProps().ref as Ref<HTMLDivElement>}>
            <Listbox {...getListboxProps()} shouldFocusOnHover={false} />
          </ScrollShadow>
          {endPopoverContent}
        </FreeSoloPopover>
      ) : null,
    [
      state.isOpen,
      getPopoverProps,
      state,
      triggerRef,
      getListboxWrapperProps,
      getListboxProps,
      onSearchValue,
      searchValue,
      onSearchChange,
    ],
  );

  return (
    <div {...getBaseProps()}>
      <HiddenSelect {...getHiddenSelectProps()} />
      {shouldLabelBeOutside ? labelContent : null}
      <div {...getMainWrapperProps()}>
        <Component {...getTriggerProps()}>
          {!shouldLabelBeOutside ? labelContent : null}
          <div {...getInnerWrapperProps()}>
            {startContent}
            <span {...getValueProps()}>{renderSelectedItem}</span>
            {endContent && state.selectedItems && <VisuallyHidden elementType="span">,</VisuallyHidden>}
            {endContent}
          </div>
          {renderIndicator}
        </Component>
        {helperWrapper}
      </div>
      {disableAnimation ? popoverContent : <AnimatePresence>{popoverContent}</AnimatePresence>}
    </div>
  );
};

export type CustomSelectProps<T extends object = object> = Props<T> & { ref?: Ref<HTMLElement> };

// forwardRef doesn't support generic parameters, so cast the result to the correct type
export default forwardRef(CustomSelect) as <T extends object>(props: CustomSelectProps<T>) => ReactElement;
